import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { Service } from 'webapp-types';
import { ActivityLogEvent, ActivityLogObjectType } from 'webapp/enums/activity-log';
import { TrackingEvent } from 'webapp/enums/tracking';

import AccountLogsTableComponent from 'webapp/components/account/logs/table';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import SubmitButtonComponent from 'webapp/components/submit-button';

import ActivityLog from 'webapp/models-custom/activity-log';

type Args = Record<string, never>;

export default class AccountLogsLockedScreenComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked submitInProgress = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.ACTIVITY_LOG_LOCKED);
	}

	get fakeActivityLogs(): ActivityLog[] {
		const userId = this.currentUserService.numericId!;
		return [
			new ActivityLog({
				message:
					'Dashboard <b>Facebook ads overview</b> has been created by <b>Katie Smith</b>',
				created: moment().subtract(4, 'minutes').unix(),
				objectId: -1,
				userId,
				objectType: ActivityLogObjectType.BOARD,
				eventType: ActivityLogEvent.BOARD_CREATED,
			}),
			new ActivityLog({
				message: '<b>John West</b> has edited this Built metric - <b>ROAS (Google ads)</b>',
				created: moment().subtract(10, 'minutes').unix(),
				objectId: -1,
				userId,
				objectType: ActivityLogObjectType.CUSTOM_QUERY,
				eventType: ActivityLogEvent.METRIC_CUSTOM_QUERY_EDITED,
			}),
			new ActivityLog({
				message:
					'<b>Luke Parsons</b> saved this report - <b>Monthly social media report</b> as an account template',
				created: moment().subtract(1, 'hours').unix(),
				objectId: -1,
				userId,
				objectType: ActivityLogObjectType.REPORT_TEMPLATE,
				eventType: ActivityLogEvent.REPORT_TEMPLATE_CREATED,
			}),
			new ActivityLog({
				message:
					'<b>Marc Berry</b> has sent the scorecard - <b>Weekly Instagram overview</b> preview to <b>marc.berry@agency.com</b>',
				created: moment().subtract(1, 'days').unix(),
				objectId: -1,
				userId,
				objectType: ActivityLogObjectType.SCORECARD,
				eventType: ActivityLogEvent.SCORECARD_CREATED,
			}),
			new ActivityLog({
				message:
					'<b>John Doe</b> has downloaded the report - <b>Monthly expenses report</b>.',
				created: moment().subtract(5, 'days').unix(),
				objectId: -1,
				userId,
				objectType: ActivityLogObjectType.REPORT,
				eventType: ActivityLogEvent.REPORT_DOWNLOADED,
			}),
		];
	}

	get canBookACall(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	@action
	openAddonPurchasePopup(): void {
		this.billingService.openActivityLogAddonPurchasePopup();
	}
	<template>
		<div class="locked-screen">
			<div class="locked-screen__content">
				<div class="locked-screen__info">
					<CommonUiIconComponent @name="ActivityLog" @size="2xl" />
					<div>
						<div class="title">
							Get access to Activity log
						</div>
						<div class="description">
							Keep tabs on your account activity with the Activity log. As an admin,
							you'll have complete visibility into the changes made to your account.
							Activity log is part of the Premium plan.
						</div>
					</div>
				</div>
				<div class="locked-screen__actions">
					<CommonUiIconComponent @name="Lock" @size="md" />
					<span>Get Advanced security management add-on</span>
					<div class="locked-screen__buttons">
						<SubmitButtonComponent
							@text="Enable"
							@onTrigger={{this.openAddonPurchasePopup}}
							@submitInProgress={{this.submitInProgress}}
						/>
						{{#if this.canBookACall}}
							<a
								role="button"
								href={{this.accountService.bookACallLink}}
								target="_blank"
							>
								Book a call
							</a>
						{{/if}}
					</div>
				</div>
			</div>

			<AccountLogsTableComponent
				@model={{this.fakeActivityLogs}}
				@extraClass="locked-screen__fake-table"
			/>
		</div>
	</template>
}
